'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from '../utils/router';
import api from '../services/api';
import ProductCard from '../components/products/ProductCard';
import { SkeletonGrid, ProductCardSkeleton } from '../components/common/SkeletonLoader';
import './HomePage.css';

const categories = [
  { key: 'all', label: '全部商品' },
  { key: 'food', label: '飼料/零食' },
  { key: 'toys', label: '玩具' },
  { key: 'accessories', label: '用品配件' },
  { key: 'clothing', label: '服飾' },
  { key: 'cage', label: '籠子/窩' },
  { key: 'feeder', label: '餵食器具' },
  { key: 'others', label: '其他' },
];

const LIMIT = 12;
let initialProductsRequest = null;
let initialProductsData = null;

function getInitialProductsData() {
  if (initialProductsData) {
    return Promise.resolve(initialProductsData);
  }

  if (!initialProductsRequest && typeof window !== 'undefined') {
    initialProductsRequest = api
      .get('/products', { params: { sort: 'newest', page: 1, limit: LIMIT } })
      .then(({ data }) => {
        initialProductsData = data;
        return data;
      })
      .catch((error) => {
        initialProductsRequest = null;
        throw error;
      });
  }

  return initialProductsRequest;
}

const HomePage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('newest');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);
  const abortRef = useRef(null);

  // Fetch products (page 1 = initial, page > 1 = load more)
  const fetchProducts = useCallback(async (pageNum, append = false) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (!append) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = { sort, page: pageNum, limit: LIMIT };
      if (category !== 'all') params.category = category;
      const isInitialDefaultFeed = !append && pageNum === 1 && category === 'all' && sort === 'newest';
      const data = isInitialDefaultFeed
        ? await getInitialProductsData()
        : (await api.get('/products', { params, signal: controller.signal })).data;

      if (append) {
        setProducts(prev => [...prev, ...data.products]);
      } else {
        setProducts(data.products);
      }
      setHasMore(pageNum < data.pagination.pages);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        console.error('Failed to fetch products:', error);
        if (!append) setProducts([]);
      }
    }
    setLoading(false);
    setLoadingMore(false);
  }, [category, sort]);

  // Reset and fetch page 1 when filters change
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setProducts([]);   // clear immediately so skeleton shows without flicker
    setLoading(true);
    fetchProducts(1, false);
  }, [category, sort, fetchProducts]);

  // Infinite scroll: IntersectionObserver on sentinel
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading && !loadingMore) {
          setPage(prev => {
            const next = prev + 1;
            fetchProducts(next, true);
            return next;
          });
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, fetchProducts]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchInput.trim())}`);
    }
  };

  return (
    <div className="home-page">
      {/* Hero */}
      <section className="curated-section">
        <div className="container">
          <div className="hero-shell">
            <div className="curated-header">
              <p className="home-eyebrow">PuiPi Select</p>
              <h1 className="curated-title">為毛孩找到<br />真正合適的好物。</h1>
              <p className="curated-subtitle">從保存良好的飼料、玩具到日常用品，讓每一次交換都更值得，也更安心。</p>
              <form className="search-bar" role="search" onSubmit={handleSearch}>
                <label htmlFor="hero-search" className="visually-hidden">搜尋寵物用品</label>
                <input
                  id="hero-search"
                  type="search"
                  className="search-input"
                  placeholder="搜尋飼料、玩具、籠子與用品配件"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  autoComplete="off"
                />
                <button type="submit" className="search-btn" aria-label="搜尋">
                  <span className="search-btn-label">開始搜尋</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19 19L14.65 14.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </form>
              <div className="hero-trust-row" aria-label="平台特色">
                <span className="hero-trust-item">安心交易</span>
                <span className="hero-trust-item">分類清晰</span>
                <span className="hero-trust-item">快速上架</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="category-section">
        <div className="container">
          <div className="category-filter">
            {categories.map((cat) => (
              <button
                key={cat.key}
                type="button"
                className={`category-btn ${category === cat.key ? 'active' : ''}`}
                onClick={() => setCategory(cat.key)}
                aria-pressed={category === cat.key}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="products-section">
        <div className="container">
          <div className="products-header">
            <div>
              <p className="section-kicker">Latest arrivals</p>
              <h2 className="section-title">最新上架</h2>
              <p className="section-subtitle">用更直覺的方式，找到適合毛孩生活節奏的二手好物。</p>
            </div>
            <label htmlFor="sort-select" className="visually-hidden">排序方式</label>
            <select id="sort-select" className="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="newest">最新上架</option>
              <option value="price-low">價格：低到高</option>
              <option value="price-high">價格：高到低</option>
              <option value="popular">最熱門</option>
            </select>
          </div>

          {loading ? (
            <SkeletonGrid count={LIMIT} />
          ) : products.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon" aria-hidden="true">
                <svg width="28" height="28" viewBox="0 0 20 20" fill="none">
                  <path d="M9 15.5C12.5899 15.5 15.5 12.5899 15.5 9C15.5 5.41015 12.5899 2.5 9 2.5C5.41015 2.5 2.5 5.41015 2.5 9C2.5 12.5899 5.41015 15.5 9 15.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M17.5 17.5L13.7 13.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3>找不到相關商品</h3>
              <p>試試其他關鍵字或分類吧</p>
            </div>
          ) : (
            <>
              <div className="products-grid">
                {products.map((product, i) => (
                  <ProductCard key={product._id} product={product} priority={i < 2} />
                ))}
                {/* Loading more skeleton cards inline */}
                {loadingMore && Array.from({ length: 4 }).map((_, i) => (
                  <ProductCardSkeleton key={`skel-${i}`} />
                ))}
              </div>

              {/* Infinite scroll sentinel */}
              {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}

              {!hasMore && products.length > LIMIT && (
                <p className="load-end-text">已顯示所有商品</p>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
